import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import styled, { keyframes } from "styled-components";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import {
  BEATS,
  ENDING_START,
  FRAME_COUNT,
  LOAD_CONCURRENCY,
  MODERN_SHARE,
  SCROLL_HEIGHT_VH,
  dateStrings,
  frameAtProgress,
  framePath,
} from "../config";
import { drawPlaceholder } from "../placeholderFrames";

gsap.registerPlugin(ScrollTrigger);

/* How wide the date cross-fade is, either side of the portal point. */
const DATE_FADE = 0.05;

/* ------------------------------------------------------------------ *
 * FRAME LOADER
 * ------------------------------------------------------------------ */

function loadImage(src) {
  return new Promise((resolve) => {
    const img = new Image();
    img.decoding = "async";
    img.onload = () => {
      if (img.decode) {
        img.decode().then(() => resolve(img)).catch(() => resolve(img));
      } else {
        resolve(img);
      }
    };
    img.onerror = () => resolve(null); // one missing frame must not kill the run
    img.src = src;
  });
}

async function loadFrames(count, onProgress, cancelled) {
  const images = new Array(count).fill(null);
  let done = 0;
  let cursor = 0;

  const worker = async () => {
    while (cursor < count) {
      if (cancelled.current) return;
      const i = cursor++;
      images[i] = await loadImage(framePath(i));
      done += 1;
      onProgress(done / count);
    }
  };

  await Promise.all(
    Array.from({ length: Math.min(LOAD_CONCURRENCY, count) }, worker)
  );

  return images;
}

/* ------------------------------------------------------------------ *
 * COMPONENT
 * ------------------------------------------------------------------ */

export default function ScrollSequence({
  frameCount = FRAME_COUNT,
  beats = BEATS,
  copy,
  lang,
  onSequenceComplete,
}) {
  const containerRef = useRef(null);
  const stageRef = useRef(null);
  const canvasRef = useRef(null);
  const ctxRef = useRef(null);
  const imagesRef = useRef([]);
  const beatRefs = useRef([]);
  const endRef = useRef(null);
  const nowRef = useRef(null);
  const thenRef = useRef(null);
  const placeholderRef = useRef(false);

  const [progress, setProgress] = useState(0);
  const [ready, setReady] = useState(false);

  const dates = useMemo(() => dateStrings(lang), [lang]);

  /* --- draw one frame, cover-fit, no distortion --- */
  const draw = useCallback(
    (index) => {
      const canvas = canvasRef.current;
      const ctx = ctxRef.current;
      if (!canvas || !ctx) return;

      const clamped = Math.max(0, Math.min(frameCount - 1, Math.round(index)));

      if (placeholderRef.current) {
        drawPlaceholder(
          ctx,
          canvas.width,
          canvas.height,
          clamped / Math.max(1, frameCount - 1),
          clamped + 1,
          frameCount
        );
        return;
      }

      let img = imagesRef.current[clamped];

      if (!img) {
        for (let i = clamped; i >= 0; i--) {
          if (imagesRef.current[i]) {
            img = imagesRef.current[i];
            break;
          }
        }
      }
      if (!img) return;

      const cw = canvas.width;
      const ch = canvas.height;
      const iw = img.naturalWidth || img.width;
      const ih = img.naturalHeight || img.height;
      if (!iw || !ih) return;

      const scale = Math.max(cw / iw, ch / ih);
      const dw = iw * scale;
      const dh = ih * scale;

      ctx.drawImage(img, (cw - dw) / 2, (ch - dh) / 2, dw, dh);
    },
    [frameCount]
  );

  /* --- size the canvas to the stage, DPR capped at 2 --- */
  const resize = useCallback(
    (currentFrame) => {
      const canvas = canvasRef.current;
      const stage = stageRef.current;
      if (!canvas || !stage) return;

      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      const w = stage.clientWidth;
      const h = stage.clientHeight;

      canvas.width = Math.round(w * dpr);
      canvas.height = Math.round(h * dpr);
      canvas.style.width = `${w}px`;
      canvas.style.height = `${h}px`;

      ctxRef.current = canvas.getContext("2d", { alpha: false });
      draw(currentFrame);
    },
    [draw]
  );

  /* --- all scroll-driven opacity, written straight to the DOM --- */
  const applyOverlays = useCallback(
    (p) => {
      // narrative beats
      beats.forEach((beat, i) => {
        const node = beatRefs.current[i];
        if (!node) return;

        const [start, end] = beat.at;
        const span = end - start;
        const fade = span * 0.22;

        let o = 0;
        if (p >= start && p <= end) {
          if (p < start + fade) o = (p - start) / fade;
          else if (p > end - fade) o = (end - p) / fade;
          else o = 1;
        }

        o = Math.max(0, Math.min(1, o));
        node.style.opacity = o;
        node.style.transform = `translateY(${(1 - o) * 14}px)`;
      });

      // date readout — today's date crosses over to the same day a
      // century ago as the player passes through the portal
      const cross = Math.max(
        0,
        Math.min(1, (p - (MODERN_SHARE - DATE_FADE)) / (DATE_FADE * 2))
      );
      if (nowRef.current) {
        nowRef.current.style.opacity = 1 - cross;
        nowRef.current.style.transform = `translateY(${cross * -10}px)`;
      }
      if (thenRef.current) {
        thenRef.current.style.opacity = cross;
        thenRef.current.style.transform = `translateY(${(1 - cross) * 10}px)`;
      }

      // ending card
      const end = endRef.current;
      if (end) {
        const eo = Math.max(
          0,
          Math.min(1, (p - ENDING_START) / (1 - ENDING_START))
        );
        end.style.opacity = eo;
        end.style.transform = `translateY(${(1 - eo) * 20}px)`;
        end.style.pointerEvents = eo > 0.9 ? "auto" : "none";
      }
    },
    [beats]
  );

  /* --- preload --- */
  useEffect(() => {
    const cancelled = { current: false };
    let mounted = true;

    loadImage(framePath(0)).then((first) => {
      if (!mounted) return;

      if (!first) {
        placeholderRef.current = true;
        setProgress(1);
        setReady(true);
        // eslint-disable-next-line
        console.warn(
          "[ScrollSequence] Frame 1 not found at " +
            framePath(0) +
            " — running in placeholder mode. Check framePath() in config.js."
        );
        return;
      }

      loadFrames(
        frameCount,
        (p) => {
          if (mounted) setProgress(p);
        },
        cancelled
      ).then((images) => {
        if (!mounted) return;
        imagesRef.current = images;
        setReady(true);
      });
    });

    return () => {
      mounted = false;
      cancelled.current = true;
    };
  }, [frameCount]);

  /* --- scroll wiring --- */
  useLayoutEffect(() => {
    if (!ready) return;

    // Stops ScrollTrigger recalculating every time the mobile address
    // bar slides away, which otherwise makes the sequence jump.
    ScrollTrigger.config({ ignoreMobileResize: true });

    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const state = { p: 0 };

    resize(0);
    applyOverlays(0);

    const ctx = gsap.context(() => {
      gsap.to(state, {
        p: 1,
        ease: "none",
        onUpdate: () => draw(frameAtProgress(state.p, frameCount)),
        scrollTrigger: {
          trigger: containerRef.current,
          start: "top top",
          end: "bottom bottom",
          scrub: reduced ? true : 0.4,
          onUpdate: (self) => applyOverlays(self.progress),
        },
      });
    }, containerRef);

    const onResize = () => resize(frameAtProgress(state.p, frameCount));
    window.addEventListener("resize", onResize);
    window.addEventListener("orientationchange", onResize);

    return () => {
      ctx.revert();
      window.removeEventListener("resize", onResize);
      window.removeEventListener("orientationchange", onResize);
    };
  }, [ready, frameCount, draw, resize, applyOverlays]);

  return (
    <>
      {!ready && (
        <Loader aria-live="polite">
          <LoaderMark />
          <LoaderText>{copy.connecting}</LoaderText>
          <LoaderTrack>
            <LoaderFill style={{ transform: `scaleX(${progress})` }} />
          </LoaderTrack>
          <LoaderCount>
            {Math.round(progress * frameCount)} / {frameCount}
          </LoaderCount>
        </Loader>
      )}

      <Container ref={containerRef} $vh={SCROLL_HEIGHT_VH}>
        <Stage ref={stageRef}>
          <Canvas ref={canvasRef} />
          <Vignette />

          <DateSlot>
            <DateLine ref={nowRef}>
              <DateLabel>{copy.today}</DateLabel>
              <DateValue>{dates.today}</DateValue>
            </DateLine>
            <DateLine ref={thenRef} style={{ opacity: 0 }} $past>
              <DateValue>{dates.past}</DateValue>
            </DateLine>
          </DateSlot>

          <BeatLayer>
            {beats.map((beat, i) => (
              <Beat
                key={i}
                ref={(el) => (beatRefs.current[i] = el)}
                style={{ opacity: 0 }}
              >
                {beat.kicker && <Kicker>{beat.kicker}</Kicker>}
                <Line>{beat.line}</Line>
              </Beat>
            ))}
          </BeatLayer>

          <EndCard ref={endRef} style={{ opacity: 0, pointerEvents: "none" }}>
            <Line>{copy.endLine}</Line>
            <Instruction>{copy.endInstruction}</Instruction>
            <CtaButton onClick={onSequenceComplete}>{copy.endCta}</CtaButton>
          </EndCard>

          {ready && <Hint>{copy.scroll}</Hint>}
        </Stage>
      </Container>
    </>
  );
}

/* ------------------------------------------------------------------ *
 * STYLES
 * ------------------------------------------------------------------ */

const Container = styled.div`
  position: relative;
  height: ${(p) => p.$vh}vh;
  background: var(--ink);
`;

const Stage = styled.div`
  position: sticky;
  top: 0;
  height: 100vh;
  height: 100dvh;
  width: 100%;
  overflow: hidden;
  background: var(--ink);
`;

const Canvas = styled.canvas`
  position: absolute;
  inset: 0;
  display: block;
`;

const Vignette = styled.div`
  position: absolute;
  inset: 0;
  pointer-events: none;
  background: radial-gradient(
      120% 80% at 50% 45%,
      transparent 40%,
      rgba(13, 11, 8, 0.72) 100%
    ),
    linear-gradient(
      to bottom,
      rgba(13, 11, 8, 0.5) 0%,
      transparent 26%,
      transparent 58%,
      rgba(13, 11, 8, 0.82) 100%
    );
`;

/* --- date readout --- */

const DateSlot = styled.div`
  position: absolute;
  top: calc(2.2rem + env(safe-area-inset-top));
  left: 0;
  right: 0;
  display: grid;
  place-items: center;
  pointer-events: none;
`;

const DateLine = styled.div`
  grid-area: 1 / 1;
  text-align: center;
  will-change: opacity, transform;
  ${(p) =>
    p.$past &&
    `
    padding-bottom: 6px;
    border-bottom: 1px solid rgba(192, 139, 62, 0.4);
  `}
`;

const DateLabel = styled.div`
  font-family: var(--font-body);
  font-size: 0.56rem;
  letter-spacing: 0.34em;
  text-transform: uppercase;
  color: var(--parchment);
  opacity: 0.4;
  margin-bottom: 0.4rem;
`;

const DateValue = styled.div`
  font-family: var(--font-display);
  font-size: 0.98rem;
  letter-spacing: 0.06em;
  color: var(--parchment);
  text-shadow: 0 1px 14px rgba(0, 0, 0, 0.9);
`;

/* --- beats --- */

const BeatLayer = styled.div`
  position: absolute;
  inset: 0;
  display: grid;
  place-items: end center;
  padding: 0 7vw calc(14vh + env(safe-area-inset-bottom));
  pointer-events: none;
`;

const Beat = styled.div`
  grid-area: 1 / 1;
  max-width: 30ch;
  text-align: center;
  will-change: opacity, transform;
`;

const Kicker = styled.div`
  font-family: var(--font-body);
  font-size: 0.7rem;
  letter-spacing: 0.28em;
  text-transform: uppercase;
  color: var(--brass);
  margin-bottom: 0.9rem;
`;

const Line = styled.p`
  margin: 0;
  font-family: var(--font-display);
  font-size: clamp(1.5rem, 6.2vw, 2.3rem);
  font-weight: 400;
  line-height: 1.32;
  color: var(--parchment);
  text-shadow: 0 2px 24px rgba(0, 0, 0, 0.85);
`;

/* --- ending --- */

const EndCard = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-end;
  text-align: center;
  padding: 0 8vw calc(9vh + env(safe-area-inset-bottom));
  background: linear-gradient(
    to bottom,
    transparent 30%,
    rgba(13, 11, 8, 0.86) 78%
  );
  will-change: opacity, transform;
`;

const Instruction = styled.p`
  margin: 1.4rem 0 0;
  max-width: 32ch;
  font-family: var(--font-body);
  font-size: 0.94rem;
  line-height: 1.62;
  color: var(--parchment-2);
`;

const CtaButton = styled.button`
  margin-top: 2.2rem;
  padding: 0.9rem 2.4rem;
  border: 1px solid rgba(192, 139, 62, 0.55);
  font-size: 0.68rem;
  letter-spacing: 0.28em;
  text-transform: uppercase;
  color: var(--brass);
  transition: background 0.3s ease;

  &:hover {
    background: rgba(192, 139, 62, 0.1);
  }
`;

const breathe = keyframes`
  0%, 100% { opacity: 0.3; transform: translateY(0); }
  50%      { opacity: 0.85; transform: translateY(5px); }
`;

const Hint = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  bottom: calc(3.5vh + env(safe-area-inset-bottom));
  text-align: center;
  font-family: var(--font-body);
  font-size: 0.64rem;
  letter-spacing: 0.32em;
  text-transform: uppercase;
  color: var(--parchment);
  animation: ${breathe} 2.6s ease-in-out infinite;
`;

/* --- loader --- */

const Loader = styled.div`
  position: fixed;
  inset: 0;
  z-index: 50;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 1.1rem;
  background: var(--ink);
  padding: 0 12vw;
`;

const pulse = keyframes`
  0%, 100% { opacity: 0.25; }
  50%      { opacity: 0.75; }
`;

const LoaderMark = styled.div`
  width: 46px;
  height: 46px;
  border: 1px solid var(--brass);
  border-radius: 50%;
  position: relative;
  animation: ${pulse} 2.4s ease-in-out infinite;

  &::after {
    content: "";
    position: absolute;
    left: 50%;
    top: 50%;
    width: 1px;
    height: 15px;
    background: var(--brass);
    transform-origin: bottom center;
    transform: translate(-50%, -100%) rotate(38deg);
  }
`;

const LoaderText = styled.div`
  font-family: var(--font-display);
  font-size: 1.05rem;
  color: var(--parchment);
  opacity: 0.85;
`;

const LoaderTrack = styled.div`
  width: min(240px, 60vw);
  height: 1px;
  background: rgba(232, 220, 196, 0.18);
  overflow: hidden;
`;

const LoaderFill = styled.div`
  height: 100%;
  width: 100%;
  background: var(--brass);
  transform-origin: left center;
  transform: scaleX(0);
  transition: transform 0.25s linear;
`;

const LoaderCount = styled.div`
  font-family: var(--font-body);
  font-size: 0.6rem;
  letter-spacing: 0.24em;
  color: var(--parchment);
  opacity: 0.35;
`;
